import { Cart, CartLine, CartLineSource, GetDeliveryMomentsResponse, LocationSilo, OrderTypes, ProductSupplement } from '@next/core-lib/cart';
import { createAction, props } from '@ngrx/store';

import {
  LocationSiloWithProductList,
  SiloProductItem,
  SiloProductItemRequest,
  SiloProductItemResponse,
} from 'src/app/components/silos/models/silo-product-list.models';
import { FavoriteSku } from 'src/app/standalone/favorites-star/favorite-sku.model';

import { GetMultipleProductDeliveryDatesWith0FlexibilityResponse } from '../models/product-delivery-moment.models';
import { GetVariant } from '../models/product-delivery.models';
import { ProductWizardTab, SkuCatalogItemViewData } from '../models/product.models';
import { SilosInUseOnDates } from '../models/silo.models';

export const addFavorite = createAction('[Product] Add favorite', props<{ skuId: number }>());

export const removeFavorite = createAction('[Product] Remove favorite', props<{ skuId: number }>());

export const getFavorites = createAction('[Product] get favorites', props<{ profileId: number }>());

export const getFavoritesSuccess = createAction('[Product] get favorites success', props<{ skus: FavoriteSku[] }>());

export const updateFavoritesSuccess = createAction('[Product] Update favorites success');

export const skuCatalogRequest = createAction('[Product] Sku catalog request', props<{ keywords: string }>());

export const skuCatalogSuccess = createAction('[Product] Sku catalog success', props<{ skuCatalogItems: SkuCatalogItemViewData[] }>());

export const skuCatalogFailure = createAction('[Product] Sku catalog failure');

export const favoritesCatalogFailure = createAction('[Product] Favorites catalog failure');

export const resetSkuCatalog = createAction('[Product] Reset sku catalog');

export const resetFavoritesCatalog = createAction('[Product] Reset favorites catalog');

export const openSearchDialog = createAction(
  '[Product] Open search dialog',
  props<{ cart?: Cart; searchTerm?: string; htmlButtonRef?: HTMLButtonElement; silo?: LocationSiloWithProductList; source: CartLineSource }>(),
);

export const openFavoritesDialog = createAction('[Product] Open favorites dialog', props<{ cart?: Cart; htmlButtonRef?: HTMLButtonElement }>());

export const openReturnSearchDialog = createAction('[Product] Open return search dialog', props<{ cart?: Cart }>());

export const openReturnPackagingSearchDialog = createAction('[Product] Open return packaging search dialog');
export const openWizardDialog = createAction(
  '[Product] Open wizard dialog',
  props<{ skuCatalogItem: SkuCatalogItemViewData; orderType?: OrderTypes; cartId?: number; source: CartLineSource }>(),
);

export const openWizardDialogForProductList = createAction(
  '[Product] Open wizard dialog for product list',
  props<{ skuCatalogItem: SkuCatalogItemViewData; silo: LocationSilo }>(),
);

export const openWizardDialogForFavorite = createAction(
  '[Product] Open wizard dialog for favorite',
  props<{ favoriteSku: FavoriteSku; orderType?: OrderTypes; cartId?: number }>(),
);

export const openWizardDialogForExistingCartLine = createAction(
  '[Product] Open wizard dialog for existing cartLine',
  props<{
    cartId: number;
    cartLine: CartLine;
    initialSelectedTab: ProductWizardTab;
    orderType?: OrderTypes;
  }>(),
);

export const getSilosWithProductListRequest = createAction('[Product] Get silos with product list request');

export const getSilosWithProductListSuccess = createAction('[Product] Get silos with product list success', props<{ silos: LocationSiloWithProductList[] }>());

export const setSilosProductSupplement = createAction(
  '[Product] Set silo product supplements request',
  props<{ siloId: number; skuId: number; supplements: ProductSupplement[] }>(),
);

export const setExpandedSiloIds = createAction('[Silo] Set Expanded Silo Ids', props<{ siloIds: number[] }>());

export const getSilosRequest = createAction('[Product] Get silos request');

export const getSilosSuccess = createAction('[Product] Get silos success', props<{ silos: LocationSilo[] }>());

export const getSilosFailed = createAction('[Product] Get silos failed');
export const getSilosWithProductListFailed = createAction('[Product] Get silos with product list failed');

export const resetSilos = createAction('[Product] Reset silos');

export const getSilosInUseOnDatesRequest = createAction('[Product] Get silos in use on dates request');
export const getSilosInUseOnDatesSuccess = createAction('[Product] Get silos in use on dates success', props<{ silosInUseOnDates: SilosInUseOnDates[] }>());

export const addSilo = createAction('[Product] Add silo', props<{ silo: LocationSilo }>());

export const updateSilo = createAction('[Product] Update silo', props<{ id: number; silo: LocationSilo }>());

export const deleteSilo = createAction('[Product] Delete silo', props<{ id: number }>());

export const deleteSiloProductListItem = createAction('[Product] Delete silo product list item', props<{ siloId: number; productId: number }>());

export const getSilosInUseOnDatesFailed = createAction('[Product] Get silos in use on dates failed');

export const resetSilosInUseOnDates = createAction('[Product] Reset silo in use on dates');

export const getSkuDeliveryMomentsRequest = createAction(
  '[Product] Get delivery moments request',
  props<{ skuId: number; orderType?: OrderTypes; flexibility?: number }>(),
);

export const getDeliveryProposalsRequest = createAction('[Product] Get delivery proposal request', props<{ skuIds: number[] }>());

export const getDeliveryProposalsRequestSuccess = createAction(
  '[Product] Get delivery proposal request success',
  props<{ proposalDeliveryMoments: GetMultipleProductDeliveryDatesWith0FlexibilityResponse }>(),
);

export const getProductInformationsRequest = createAction('[Product] Get product information', props<{ skuIds: number[] }>());

export const getProductInformationsRequestSuccess = createAction(
  '[Product] Get product information success',
  props<{ productInformation: SkuCatalogItemViewData[] }>(),
);

export const getDeliveryMomentsSuccess = createAction('[Product] Get delivery moments success', props<{ deliveryMoments: GetDeliveryMomentsResponse }>());

export const getDeliveryMomentsFailed = createAction('[Product] Get delivery moments failed');

export const resetDeliveryMoments = createAction('[Product] Reset delivery moments');

export const getVariantsRequest = createAction('[Product] Get variants request');

export const getVariantsSuccess = createAction('[Product] Get variants success', props<{ variants: GetVariant[] }>());

export const getVariantsFailed = createAction('[Product] Get variants failed');

export const resetVariants = createAction('[Product] Reset variants');

export const getCartDeliveryDatesRequest = createAction(
  '[Cart] Get delivery dates request',
  props<{ cartId: number; orderType?: OrderTypes; flexibility?: number }>(),
);

export const patchCartDeliveryDateAndFlexibilityRequest = createAction(
  '[Cart] Patch delivery date and flexibility request',
  props<{ cartId: number; deliveryDate: string; flexibility?: number }>(),
);

export const getCartLineDeliveryMomentsRequest = createAction(
  '[Product] Get cart line delivery moments request',
  props<{ cartId: number; flexibility?: number }>(),
);

export const getCartLineDeliveryMomentsSuccess = createAction(
  '[Product] Get cart line delivery moments success',
  props<{ deliveryMoments: GetDeliveryMomentsResponse }>(),
);

export const getCartLineDeliveryMomentsFailed = createAction('[Product] Get cart line delivery moments failed');

export const resetCartLineDeliveryMoments = createAction('[Product] Reset cart line delivery moments');

export const preloadSkuSuccess = createAction('[Product] Preload sku success');

export const setSelectedCartLine = createAction('[Product] Set selected cart line', props<{ selectedCartLine: CartLine }>());

export const resetSelectedCartLine = createAction('[Product] Reset selected cart line');

export const setSelectedProductSiloLine = createAction('[Product] Set selected product silo line', props<{ selectedProductSiloLine: SiloProductItem }>());

export const resetSelectedProductSiloLine = createAction('[Product] Reset selected product silo line');

export const addProductToSiloProductListRequest = createAction(
  '[Product] Add product to silo product list request',
  props<{ request: SiloProductItemRequest }>(),
);

export const addProductToSiloProductListSuccess = createAction(
  '[Product] Add product to silo product list success',
  props<{ response: SiloProductItemResponse[] }>(),
);

export const addProductToSiloProductListFailed = createAction('[Product] Add product to silo product list failed');
